import { Link } from '@tanstack/react-router'
import { captureSource } from './attribution'
import { BRAND_FALLBACK, usePublicConfig } from './config'

// Record utm params / referrer as soon as any marketing page loads, before in-app navigation drops them.
captureSource()

export function Mark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" className={className} fill="currentColor">
      <path d="M4 23 3.2 18.6l3.6.4-1-4.6 3.8 1.3.1-4.9 3.7 2.4 1.6-4.6 2.9 3.6 2.7-4 1.6 4.6 3-2.1.3 4.3c2.3.9 4.3 2.9 5.2 5.6.2.6-.1 1.2-.7 1.4-1.7.6-3.4 1.5-5 2.6-.8.6-1.7.9-2.6.9H4Z" />
      <circle cx="23.6" cy="17.4" r="1.15" fill="var(--mark-eye, #1f5c46)" />
    </svg>
  )
}

export function useBrand(): string {
  return usePublicConfig().config?.brand ?? BRAND_FALLBACK
}

export function Wordmark({ className }: { className?: string }) {
  const brand = useBrand()
  return (
    <Link to="/" className={`inline-flex items-center gap-2 rounded-sm ${className ?? ''}`}>
      <Mark className="size-8" />
      <span className="text-[1.3125rem] font-extrabold tracking-[-0.02em] [font-stretch:120%]">{brand}</span>
    </Link>
  )
}

export function SkipLink() {
  return (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:rounded-md focus:bg-white focus:px-4 focus:py-2.5 focus:font-semibold focus:text-foreground focus:shadow-lg"
    >
      Skip to main content
    </a>
  )
}

/** `width` lets product screens align the footer with their own content column. */
export function SiteFooter({ width = 'max-w-6xl sm:px-6' }: { width?: string }) {
  const { config } = usePublicConfig()
  const brand = config?.brand ?? BRAND_FALLBACK
  return (
    <footer className="border-t border-border bg-white">
      <div className={`mx-auto grid gap-8 px-4 py-12 md:grid-cols-[1fr_auto] md:items-end ${width}`}>
        <div>
          <Wordmark className="text-primary [--mark-eye:#fff]" />
          <p className="mt-3 max-w-md text-[0.9375rem] text-muted-foreground">
            WordPress sites for small businesses, rebuilt with the same design on a fast, secure platform.
          </p>
          {config?.supportEmail && (
            <p className="mt-3 text-[0.9375rem]">
              Questions?{' '}
              <a className="text-link font-medium" href={`mailto:${config.supportEmail}`}>
                {config.supportEmail}
              </a>
            </p>
          )}
        </div>
        <nav aria-label="Legal" className="text-[0.9375rem]">
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            <li>
              <Link to="/terms" className="text-link">
                Terms
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="text-link">
                Privacy
              </Link>
            </li>
            <li>
              <Link to="/subprocessors" className="text-link">
                Subprocessors
              </Link>
            </li>
          </ul>
          <p className="mt-3 text-sm text-muted-foreground">© {new Date().getFullYear()} {brand}</p>
        </nav>
      </div>
    </footer>
  )
}
